import type { FC } from 'react'
import type { ProjectSummary } from '@/types'

import { ArrowUpRight } from 'lucide-react'
import { PageLink } from '@/components/kit/page-link'
import { Photo } from '@/components/kit/photo'

/**
 * Full-width link to the next case study. On hover its cover grows from a
 * small window until it fills the whole panel.
 */
export const NextProject: FC<{ project: ProjectSummary }> = ({ project }) => {
    return (
        <section
            aria-label='Next case note'
            className='px-3 pt-6 md:px-5'
        >
            <PageLink
                href={`/work/${project.slug}`}
                data-cursor='Open'
                className='group relative flex h-[78vh] min-h-[460px] items-end overflow-hidden rounded-[2rem] bg-ink text-cream md:rounded-[3rem]'
            >
                <div className='absolute inset-0 transition-[clip-path] duration-[1.2s] ease-expo [clip-path:inset(22%_24%_22%_24%_round_2rem)] group-hover:[clip-path:inset(0%_0%_0%_0%_round_0rem)] group-focus-visible:[clip-path:inset(0%_0%_0%_0%_round_0rem)] max-md:[clip-path:inset(18%_10%_30%_10%_round_1.5rem)]'>
                    {project.cover && (
                        <Photo
                            src={project.cover}
                            alt={project.cover_alt ?? ''}
                            sizes='100vw'
                            grain
                            className='absolute inset-0 bg-ink'
                            imgClassName='scale-110 transition-transform duration-[1.4s] ease-expo group-hover:scale-100'
                        />
                    )}
                    <div className='absolute inset-0 bg-black/25 transition-colors duration-700 group-hover:bg-black/45' />
                </div>

                <div className='relative z-10 flex w-full flex-col gap-6 p-6 md:flex-row md:items-end md:justify-between md:p-12'>
                    <div>
                        <p className='font-mono text-xs uppercase tracking-[0.18em] text-cream/70'>
                            Next case note
                        </p>
                        <p className='mt-3 font-heading text-[clamp(3rem,9vw,8.5rem)] font-bold leading-[0.86] tracking-[-0.04em]'>
                            <span data-mdlfy-cms={`projects:${project.id}:brand`}>{project.brand}</span>
                        </p>
                        {project.headline_result && (
                            <p className='mt-4 inline-flex rounded-full bg-ember px-4 py-1.5 text-sm font-semibold text-ink'>
                                <span data-mdlfy-cms={`projects:${project.id}:headline_result`}>{project.headline_result}</span>
                            </p>
                        )}
                    </div>

                    <span className='flex size-16 shrink-0 items-center justify-center rounded-full bg-cream text-ink transition-transform duration-500 ease-expo group-hover:rotate-45 md:size-20'>
                        <ArrowUpRight className='size-6' />
                    </span>
                </div>
            </PageLink>
        </section>
    )
}
